import React, { useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import { Chart, ArcElement, DoughnutController, Tooltip } from "chart.js";

Chart.register(ArcElement, DoughnutController, Tooltip);

const SEGMENTS = [
  { key: "count100", label: "1.0 FTE" },
  { key: "count75",  label: "0.75 FTE" },
  { key: "count50",  label: "0.50 FTE" },
  { key: "count25",  label: "0.25 FTE" },
];

const FteDonut = ({ fteDistribution, monthLabel }) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const isDark = theme.palette.mode === "dark";
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  const COLORS = {
    light: ["#28B3B4", "#378ADD", "#D6DF27", "#639922"],
    dark:  ["#28B3B4", "#85B7EB", "#D6DF27", "#97C459"],
  };
  const colors = isDark ? COLORS.dark : COLORS.light;

  useEffect(() => {
    if (!fteDistribution || !canvasRef.current) return;

    const { count100 = 0, count75 = 0, count50 = 0, count25 = 0 } = fteDistribution;
    const data = [count100, count75, count50, count25];
    const total = data.reduce((a, b) => a + b, 0) || 1;

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: SEGMENTS.map(s => s.label),
        datasets: [{
          data,
          backgroundColor: colors,
          borderColor: "transparent",
          borderWidth: 0,
          hoverOffset: 4,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "70%",
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: t.surface2,
            borderColor: t.border2,
            borderWidth: 1,
            titleColor: t.text,
            bodyColor: t.text2,
            padding: 10,
            callbacks: {
              label: (ctx) => {
                const pct = ((ctx.parsed / total) * 100).toFixed(1);
                return `  ${ctx.parsed} allocations (${pct}%)`;
              },
            },
          },
        },
      },
    });

    return () => { chartRef.current?.destroy(); };
  }, [fteDistribution, isDark]);

  if (!fteDistribution) return null;

  const { totalFte = 0, count100 = 0, count75 = 0, count50 = 0, count25 = 0 } = fteDistribution;
  const counts = [count100, count75, count50, count25];

  return (
    <div style={{
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: 12,
      overflow: "hidden",
    }}>

      {/* header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 18px",
        borderBottom: `1px solid ${t.border}`,
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>FTE Distribution</span>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: "2px 8px",
          borderRadius: 20, background: t.accent2, color: t.accent,
        }}>
          {monthLabel}
        </span>
      </div>

      {/* body */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 20,
        padding: "20px 18px",
      }}>

        {/* donut + center label */}
        <div style={{ position: "relative", width: 130, height: 130, flexShrink: 0,zIndex: 1 }}>
          <canvas ref={canvasRef} />
          {/* center text */}
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            pointerEvents: "none",
            zIndex: 0,
          }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: t.text, lineHeight: 1 }}>
              {Number(totalFte).toFixed(1)}
            </span>
            <span style={{ fontSize: 10, color: t.text3, marginTop: 3 }}>total FTE</span>
          </div>
        </div>

        {/* legend */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
          {SEGMENTS.map(({ label }, i) => {
            const count = counts[i];
            const total = counts.reduce((a, b) => a + b, 0) || 1;
            const pct   = Math.round((count / total) * 100);
            return (
              <div key={label} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{
                  display: "flex", alignItems: "center",
                  justifyContent: "space-between",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: colors[i], flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 11, color: t.text2 }}>{label}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: t.text }}>{count}</span>
                    <span style={{ fontSize: 10, color: t.text3 }}>{pct}%</span>
                  </div>
                </div>
                {/* mini progress bar */}
                <div style={{
                  height: 3, borderRadius: 2,
                  background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                }}>
                  <div style={{
                    height: "100%", borderRadius: 2,
                    width: `${pct}%`,
                    background: colors[i],
                    transition: "width .4s ease",
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FteDonut;